import React, { useState } from "react";
import styles from "./information_form.module.css";
import { Col, Row, Select, Typography } from "antd";
import { Controller, useWatch } from "react-hook-form";
import CustomTextfield from "../../../../common/input/custom-textfield/custom-textfield";
import CustomSelect from "../../../../common/input/custom-select/custom-select";
import CustomDatepicker from "../../../../common/input/custom-datepicker/custom-datepicker";
import { MailOutlined, PhoneOutlined } from "@ant-design/icons";
import { RELATIONSHIP_OPTIONS } from "../../../../constants/common";
import { getSectionsByLevelId } from "../../../../services/parent_form";
import dayjs from "dayjs";

const InformationForm = ({ control, classes, schoolName }) => {
  const [sections, setSections] = useState([]);

  const regNo = useWatch({ control, name: "student_reg_number" });
  const isStudentInfoRequired = !regNo;

  const studentFirstName = useWatch({ control, name: "student_first_name" });
  const studentLastName = useWatch({ control, name: "student_last_name" });
  const studentDob = useWatch({ control, name: "student_date_of_birth" });
  const studentLevel = useWatch({ control, name: "student_level_jd" });
  const studentSection = useWatch({ control, name: "student_section_id" });

  const allStudentFieldsFilled =
    studentFirstName &&
    studentLastName &&
    studentDob &&
    studentLevel &&
    studentSection;

  const isRegNumberRequired = !allStudentFieldsFilled;

  const handleLevelChange = async (levelName) => {
    if (levelName) {
      try {
        const payload = {
          institution_name: schoolName,
          level_name: levelName,
        };
        const res = await getSectionsByLevelId(payload);
        const data = res?.data?.data || [];
        setSections(data);
      } catch (err) {
        setSections([]);
      }
    }
  };

  return (
    <div>
      <div>
        <div className={styles.headerContainer}>
          <Typography className={styles.title}>Student Information</Typography>
        </div>
        <Row gutter={[32, 20]}>
          <Col span={12}>
            <Controller
              name="student_reg_number"
              control={control}
              rules={{ required: isRegNumberRequired }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomTextfield
                  id="student_reg_number"
                  label="Student Registration ID"
                  showRequired
                  labelColor="#171717"
                  placeholder="Enter registration number"
                  size="large"
                  value={value}
                  onChange={onChange}
                  status={error ? "error" : ""}
                />
              )}
            />
          </Col>

          <Col span={12}>
            <Controller
              name="student_first_name"
              control={control}
              rules={{ required: isStudentInfoRequired }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomTextfield
                  id="student_first_name"
                  label="Student First Name"
                  showRequired
                  labelColor="#171717"
                  placeholder="Enter name"
                  size="large"
                  value={value}
                  onChange={(e) => {
                    const input = e.target.value;
                    const capitalized =
                      input.charAt(0).toUpperCase() + input.slice(1);
                    onChange(capitalized);
                  }}
                  status={error ? "error" : ""}
                />
              )}
            />
          </Col>

          <Col span={12}>
            <Controller
              name="student_last_name"
              control={control}
              rules={{ required: isStudentInfoRequired }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomTextfield
                  id="student_last_name"
                  label="Student Last Name"
                  showRequired
                  labelColor="#171717"
                  placeholder="Enter last name"
                  size="large"
                  value={value}
                  onChange={(e) => {
                    const input = e.target.value;
                    const capitalized =
                      input.charAt(0).toUpperCase() + input.slice(1);
                    onChange(capitalized);
                  }}
                  status={error ? "error" : ""}
                />
              )}
            />
          </Col>
          <Col span={12}>
            <Controller
              name="student_date_of_birth"
              control={control}
              rules={{ required: isStudentInfoRequired }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomDatepicker
                  id="student_date_of_birth"
                  label="Date of Birth"
                  showRequired
                  labelColor="#171717"
                  placeholder="DD-MM-YYYY"
                  size="large"
                  value={value}
                  onChange={onChange}
                  style={{ height: "40px", width: "100%" }}
                  status={error ? "error" : ""}
                  prefix={
                    <img
                      src="/assets/images/calendarOutlined.svg"
                      style={{ marginRight: 4 }}
                      alt="calendar"
                    />
                  }
                  disabledDate={(current) =>
                    current && current > dayjs().endOf("day")
                  }
                />
              )}
            />
          </Col>
          <Col span={12}>
            <Controller
              name="student_level_jd"
              control={control}
              rules={{ required: isStudentInfoRequired }}
              render={({ field, fieldState: { error } }) => (
                <CustomSelect
                  id="student_level_jd"
                  label="Student Class"
                  showRequired
                  labelColor="#171717"
                  placeholder="--Select--"
                  size="large"
                  value={field.value}
                  onChange={(levelId) => {
                    const selected = classes.find((cls) => cls.id === levelId);
                    const levelName = selected?.name;
                    handleLevelChange(levelName);
                    field.onChange(levelId);
                  }}
                  status={error ? "error" : ""}
                >
                  {classes.map((item) => (
                    <Select.Option key={item.id} value={item?.id}>
                      {item?.name}
                    </Select.Option>
                  ))}
                </CustomSelect>
              )}
            />
          </Col>
          <Col span={12}>
            <Controller
              name="student_section_id"
              control={control}
              rules={{ required: isStudentInfoRequired }}
              render={({ field, fieldState: { error } }) => (
                <CustomSelect
                  id="student_section_id"
                  label="Student Section"
                  showRequired
                  labelColor="#171717"
                  placeholder="--Select--"
                  size="large"
                  {...field}
                  status={error ? "error" : ""}
                >
                  {sections.map((item, index) => (
                    <Select.Option key={index} value={item?.id}>
                      {item?.name}
                    </Select.Option>
                  ))}
                </CustomSelect>
              )}
            />
          </Col>
        </Row>
      </div>

      <div style={{ marginTop: "20px" }}>
        <div className={styles.headerContainer}>
          <Typography className={styles.title}>Parent Information</Typography>
        </div>
        <Row gutter={[32, 20]}>
          <Col span={12}>
            <Controller
              name="first_name"
              control={control}
              rules={{ required: true }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomTextfield
                  id="first_name"
                  label="Parent First Name"
                  showRequired
                  labelColor="#171717"
                  placeholder="Enter name"
                  size="large"
                  value={value}
                  onChange={(e) => {
                    const input = e.target.value;
                    const capitalized =
                      input.charAt(0).toUpperCase() + input.slice(1);
                    onChange(capitalized);
                  }}
                  status={error ? "error" : ""}
                />
              )}
            />
          </Col>

          <Col span={12}>
            <Controller
              name="last_name"
              control={control}
              rules={{ required: true }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomTextfield
                  id="last_name"
                  label="Parent Last Name"
                  showRequired
                  labelColor="#171717"
                  placeholder="Enter last name"
                  size="large"
                  value={value}
                  onChange={(e) => {
                    const input = e.target.value;
                    const capitalized =
                      input.charAt(0).toUpperCase() + input.slice(1);
                    onChange(capitalized);
                  }}
                  status={error ? "error" : ""}
                />
              )}
            />
          </Col>
          <Col span={12}>
            <Controller
              name="date_of_birth"
              control={control}
              rules={{ required: true }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomDatepicker
                  id="date_of_birth*"
                  label="Date of Birth"
                  showRequired
                  labelColor="#171717"
                  placeholder="DD-MM-YYYY"
                  size="large"
                  value={value}
                  onChange={onChange}
                  style={{ height: "40px", width: "100%" }}
                  status={error ? "error" : ""}
                  prefix={
                    <img
                      src="/assets/images/calendarOutlined.svg"
                      style={{ marginRight: 4 }}
                      alt="calendar"
                    />
                  }
                  disabledDate={(current) =>
                    current && current > dayjs().endOf("day")
                  }
                />
              )}
            />
          </Col>
          <Col span={12}>
            <Controller
              name="national_id"
              control={control}
              rules={{ required: true }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomTextfield
                  id="national_id"
                  label="National Id"
                  showRequired
                  labelColor="#171717"
                  placeholder="Enter national id"
                  size="large"
                  value={value}
                  onChange={onChange}
                  status={error ? "error" : ""}
                />
              )}
            />
          </Col>
          <Col span={12}>
            <Controller
              name="phone_number"
              control={control}
              rules={{ required: true }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomTextfield
                  id="phone_number"
                  label="Phone Number"
                  showRequired
                  labelColor="#171717"
                  placeholder="Enter Phone Number"
                  type="number"
                  size="large"
                  value={value}
                  prefix={
                    <PhoneOutlined
                      style={{ color: "#999999", paddingRight: 4 }}
                    />
                  }
                  onChange={onChange}
                  status={error ? "error" : ""}
                />
              )}
            />
          </Col>
          <Col span={12}>
            <Controller
              name="email"
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomTextfield
                  id="email"
                  label="Email Address"
                  showOptional
                  type="email"
                  labelColor="#171717"
                  placeholder="Enter Email address"
                  size="large"
                  value={value}
                  prefix={
                    <MailOutlined
                      style={{ color: "#999999", paddingRight: 4 }}
                    />
                  }
                  onChange={onChange}
                  status={error ? "error" : ""}
                />
              )}
            />
          </Col>
          <Col span={12}>
            <Controller
              name="parent_type"
              control={control}
              rules={{ required: true }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomSelect
                  id="parent_type"
                  label="Relation To Student"
                  showRequired
                  labelColor="#171717"
                  placeholder="--Select--"
                  size="large"
                  value={value}
                  onChange={onChange}
                  status={error ? "error" : ""}
                >
                  {RELATIONSHIP_OPTIONS.map((item, index) => (
                    <Select.Option key={index} value={item?.value}>
                      {item?.label}
                    </Select.Option>
                  ))}
                </CustomSelect>
              )}
            />
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default InformationForm;
